import type { SxProps, Theme } from '@mui/material/styles';

import { Stack } from 'ui/stack';
import { Box } from 'ui/box';
import { mergeSx } from 'ui/merge-sx';

export interface FunnelCardSkeletonProps {
  titleSx: SxProps<Theme>;
}

const BAR_PAIR_COUNT = 2;
const BAR_PAIRS = Array.from({ length: BAR_PAIR_COUNT }, (_, index) => index);

const COMPARATOR_ROW_COUNT = 3;
const COMPARATOR_ROWS = Array.from(
  { length: COMPARATOR_ROW_COUNT },
  (_, index) => index
);

const FunnelCardSkeleton = ({ titleSx }: FunnelCardSkeletonProps) => (
  <Stack
    sx={{
      width: '100%',
      borderRadius: '0.5rem',
      borderWidth: '1px',
      borderColor: 'tokens.navy.100',
      bgcolor: 'common.white',
    }}
    data-testid="funnel-card-skeleton"
  >
    <Stack
      sx={{
        alignItems: 'flex-start',
        gap: 1,
        borderBottomWidth: '1px',
        borderColor: 'tokens.navy.100',
        px: 1.5,
        pt: 1,
        pb: '9px',
      }}
    >
      <Box
        component="span"
        sx={mergeSx(
          {
            display: 'block',
            height: '1.25rem',
            borderRadius: '0.25rem',
            bgcolor: 'tokens.gray.200',
          },
          titleSx
        )}
      />
    </Stack>

    <Box sx={{ px: 1.5, py: 1.25 }}>
      <Stack direction="row" sx={{ alignItems: 'center', gap: 0.75 }}>
        <span className="block h-7 w-16 rounded bg-gray-200" />
        <span className="block h-6 w-14 rounded bg-gray-100" />
      </Stack>
    </Box>

    <Stack sx={{ gap: 2, px: 1.5, py: 1 }}>
      {BAR_PAIRS.map((barPair) => (
        <Stack key={barPair} sx={{ gap: 1 }}>
          <Stack
            direction="row"
            sx={{ alignItems: 'center', justifyContent: 'space-between' }}
          >
            <span className="block h-4 w-24 rounded bg-gray-100" />
            <span className="block h-4 w-10 rounded bg-gray-100" />
          </Stack>
          <Box
            sx={{
              height: '0.625rem',
              borderRadius: '9999px',
              bgcolor: 'tokens.gray.100',
            }}
          />
        </Stack>
      ))}
    </Stack>

    <Stack sx={{ flex: '1 1 0%', gap: 0.5, px: 1.5, py: 1 }}>
      {COMPARATOR_ROWS.map((row) => (
        <Stack
          key={row}
          direction="row"
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            borderRadius: '0.25rem',
            bgcolor: 'tokens.gray.50',
            borderWidth: '1px',
            borderColor: 'tokens.gray.100',
            px: 1,
            py: '3px',
          }}
        >
          <span className="block h-4 w-24 rounded bg-gray-200" />
          <span className="block h-4 w-10 rounded bg-gray-200" />
        </Stack>
      ))}
    </Stack>
  </Stack>
);

export default FunnelCardSkeleton;
